import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function PrivateRoute({ children, requireOnboarding = true }) {
  const { currentUser, userData, loading, isSubscriptionActive, getSubscriptionStatus } = useAuth();

  // Show loading while checking auth state (prevents flash of login screen)
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading if user data isn't loaded yet
  if (currentUser && !userData) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your HEIMDL data...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/gatekeeper" replace />;
  }

  // Check subscription status
  const subscriptionStatus = getSubscriptionStatus();
  
  // Handle expired subscriptions (except for onboarding and settings)
  if (subscriptionStatus.status === 'expired' && requireOnboarding) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white rounded-xl shadow-xl p-8 border border-red-200">
          <div className="text-6xl mb-4">⏰</div>
          <h2 className="text-2xl font-bold text-red-800 mb-4">Subscription Expired</h2>
          <p className="text-red-600 mb-6">
            Your {userData.subscription} subscription has expired. 
            Please contact support or upgrade your plan to continue using HEIMDL.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = 'mailto:support@heimdlcatalog.com'}
              className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
            >
              Contact Support
            </button>
            <button
              onClick={() => window.location.href = '/gatekeeper'}
              className="w-full bg-slate-500 text-white px-4 py-2 rounded-lg hover:bg-slate-600"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if onboarding is required and completed
  if (requireOnboarding && !userData.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  // Allow access to onboarding even if not completed
  if (!requireOnboarding) {
    return children;
  }

  // Show subscription warning for trial users approaching expiration
  const showTrialWarning = userData.subscription === 'trial' && 
                          subscriptionStatus.daysRemaining !== null && 
                          subscriptionStatus.daysRemaining <= 7;

  return (
    <div>
      {/* Trial expiration warning banner */}
      {showTrialWarning && (
        <div className="bg-yellow-500 text-black px-4 py-2 text-center text-sm font-medium">
          ⚠️ Trial expires in {subscriptionStatus.daysRemaining} days. 
          <a href="mailto:support@heimdlcatalog.com" className="underline ml-2">
            Contact us to upgrade
          </a>
        </div>
      )}
      
      {children}
    </div>
  );
}

export default PrivateRoute;